/**
 * Pipe tables in chat Markdown (shared by the web and mobile renderers).
 *
 * The assistant writes GitHub-style tables for anything with columns of
 * numbers ("profit pe proiect"): a header row, a separator row
 * (|---|:--:|---:|) and body rows. Rendered as raw text they are unreadable,
 * so both chat renderers detect them with this module and draw real tables.
 *
 * Dependency-free on purpose: mobile imports it through `@web/lib`.
 */

export type TableAlign = 'left' | 'right' | 'center';

export interface ParsedTable {
  headers: string[];
  align: TableAlign[];
  rows: string[][];
  /** Index of the first line after the table. */
  end: number;
}

const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
// A cell "looks numeric" when it is a number, a percentage or an amount with a
// unit (192.868, -714 RON, 80%, 1.550,00 RON); "n/a" does not count.
const NUMERIC_CELL_RE = /^[-+−]?\s*[\d.,\s]+\s*(%|[A-Z]{3}|lei|ore|h|zile)?$/i;

/** Split one table line into trimmed cells; `\|` inside a cell stays a pipe. */
export function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '\\' && s[i + 1] === '|') {
      current += '|';
      i += 1;
    } else if (ch === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

export function isTableStart(lines: string[], i: number): boolean {
  return TABLE_ROW_RE.test(lines[i]) && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]);
}

/** Column alignment: explicit from the separator colons, else right for numeric columns. */
function tableAlignments(sep: string, headers: string[], rows: string[][]): TableAlign[] {
  const specs = splitTableRow(sep);
  return headers.map((_, c) => {
    const spec = specs[c] ?? '';
    if (spec.startsWith(':') && spec.endsWith(':')) return 'center';
    if (spec.endsWith(':')) return 'right';
    if (spec.startsWith(':')) return 'left';
    const cells = rows.map((r) => r[c] ?? '').filter((v) => v !== '');
    const numeric = cells.filter((v) => NUMERIC_CELL_RE.test(v.replace(/\*\*/g, ''))).length;
    return cells.length > 0 && numeric >= Math.ceil(cells.length / 2) ? 'right' : 'left';
  });
}

/** Parse the table starting at `lines[start]` (caller checked `isTableStart`). */
export function parseTable(lines: string[], start: number): ParsedTable {
  const headers = splitTableRow(lines[start]);
  const rows: string[][] = [];
  let i = start + 2;
  while (i < lines.length && TABLE_ROW_RE.test(lines[i])) {
    const cells = splitTableRow(lines[i]);
    while (cells.length < headers.length) cells.push('');
    rows.push(cells.slice(0, headers.length));
    i += 1;
  }
  return { headers, align: tableAlignments(lines[start + 1], headers, rows), rows, end: i };
}
